import { Tooltip, useElementSize } from '@byecode/ui'
import { clamp } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCounter, useUpdateEffect } from 'react-use'
import styled from 'styled-components'

interface SliderProps {
    value?: number
    disabled?: boolean
    min: number
    max: number
    step: number
    bgColor?: string
    color?: string
    onChange?: (v: number) => void
}

const SCxInactiveArea = styled.div<{ sliderWidth: number; color?: string; bgColor?: string; disabled?: boolean }>`
    flex: 1;
    align-self: stretch;
    display: flex;
    align-items: center;
    touch-action: none;
    user-select: none;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'unset')};

    --slider-thumb-size: 12px;
    --slider-border-size: 3px;
    --slider-width: ${({ sliderWidth }) => `${sliderWidth}px`};
    --slider-track-bg: ${({ color }) => color || 'var(--color-app-main)'};
    --slider-bg: ${({ bgColor }) => bgColor || 'var(--color-gray-200)'};
`

const SCxContainer = styled.div`
    position: relative;
    width: 100%;
    height: 5px;
    background-color: var(--slider-bg);
    border-radius: 10px;
    &[aria-disabled='true'] {
        background-color: var(--color-gray-100);
        cursor: not-allowed;
    }
`

const SCxSliderTrack = styled.div`
    position: absolute;
    inset: 0;
    height: 5px;
    width: var(--slider-width);
    background-color: var(--slider-track-bg);
    border-radius: 10px;
    transition: all 0.2s ease-in-out;
    z-index: 1;

    &[aria-disabled='true'] {
        background-color: var(--color-gray-200);
        cursor: not-allowed;
    }
`

const SCxSliderMark = styled.div`
    position: absolute;
    right: calc((var(--slider-thumb-size)) / -2);
    top: calc((var(--slider-thumb-size) - (var(--slider-border-size) * 2)) / -2);
    bottom: 0;
    z-index: 2;
    width: var(--slider-thumb-size);
    height: var(--slider-thumb-size);
    border: var(--slider-border-size) solid var(--slider-track-bg);
    background-color: var(--color-white);
    border-radius: 100%;
    cursor: pointer;
    &[aria-disabled='true'] {
        border-color: var(--color-gray-200);
    }
`

function getShare(stepWidth: number, deltaX: number) {
    const isOffset = Math.abs(deltaX % stepWidth) > stepWidth / 2
    const modifyOffset = isOffset ? (deltaX > 0 ? 1 : -1) : 0
    const computeX = Math.floor(Math.abs(deltaX / stepWidth))
    return (deltaX > 0 ? computeX : -computeX) + modifyOffset
}

export const Slider: React.FunctionComponent<SliderProps> = ({ value = 0, min, max, step = 1, color, bgColor, disabled, onChange }) => {
    const { ref: containerRef, width: containerWidth } = useElementSize<HTMLDivElement>()
    const markRef = useRef<HTMLDivElement>(null)
    const initMarkXRef = useRef(0)
    const [data, { set: setData }] = useCounter(value, max, min)
    /** x轴偏移量 */
    const [deltaX, setDeltaX] = useState(0)
    /** 是否拖拽结束  */
    const isDraggingRef = useRef(false)
    /** 总共可以偏移的份数 */
    const totalNum = useMemo(() => Math.floor((max - min) / step), [max, min, step])
    /** 每一份所占宽度  */
    const stepWidth = useMemo(() => Number((containerWidth / totalNum).toFixed(4)), [containerWidth, totalNum])
    /** 当前可以偏移的份数  */
    const currentShare = useMemo(() => Math.floor((data - min) / step), [data, min, step])
    /** 根据step计算出的当前偏移份数 */
    const shareX = useMemo(() => getShare(stepWidth, deltaX), [deltaX, stepWidth])
    /** 当前SliderTracker 所占宽度  */
    const sliderWidth = useMemo(
        () => Math.max(clamp(0, totalNum, currentShare + shareX) * stepWidth, 0),
        [currentShare, shareX, stepWidth, totalNum]
    )
    /** 计算最终值 */
    const finallyValue = useMemo(() => {
        return clamp(min, max, Math.floor((data + shareX * step) / step) * step)
    }, [data, max, min, shareX, step])

    useUpdateEffect(() => {
        setData(value)
    }, [value])

    const handleMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (disabled) {
                return
            }
            const deltaX = e.pageX - (containerRef.current?.getBoundingClientRect().x ?? 0)
            const newData = getShare(stepWidth, deltaX) * step
            setData(newData)
            onChange?.(newData)
        },
        [containerRef, disabled, onChange, setData, step, stepWidth]
    )

    const handleStart = useCallback(() => {
        isDraggingRef.current = true
        initMarkXRef.current = markRef.current?.getBoundingClientRect().x ?? 0 - 12
    }, [])
    const handleMove = useCallback((e: MouseEvent | TouchEvent) => {
        requestAnimationFrame(() => {
            if (!isDraggingRef.current) {
                return
            }
            const markX = initMarkXRef.current
            const pointerX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX
            setDeltaX(pointerX - markX)
        })
    }, [])

    const handleEnd = useCallback(() => {
        if (!isDraggingRef.current) {
            return
        }
        setData(finallyValue)
        onChange?.(finallyValue)
        setDeltaX(0)
        isDraggingRef.current = false
    }, [finallyValue, onChange, setData])

    useEffect(() => {
        if (disabled) {
            return
        }
        containerRef.current?.addEventListener('mousedown', handleStart, { passive: true })
        document?.addEventListener('mousemove', handleMove, { passive: true })
        document?.addEventListener('mouseup', handleEnd)
        containerRef.current?.addEventListener('touchstart', handleStart, { passive: true })
        document?.addEventListener('touchmove', handleMove, { passive: true })
        document?.addEventListener('touchend', handleEnd)

        return () => {
            containerRef.current?.removeEventListener('mousedown', handleStart)
            document?.removeEventListener('mousemove', handleMove)
            document?.removeEventListener('mouseup', handleEnd)

            containerRef.current?.removeEventListener('touchstart', handleStart)
            document?.removeEventListener('touchmove', handleMove)
            document?.removeEventListener('touchend', handleEnd)
        }
    }, [containerRef, disabled, handleEnd, handleMove, handleStart])
    return (
        <SCxInactiveArea
            color={color}
            bgColor={bgColor}
            disabled={disabled}
            sliderWidth={sliderWidth}
            onMouseDown={handleMouseDown}
            ref={containerRef}
        >
            <SCxContainer aria-disabled={disabled}>
                <SCxSliderTrack aria-disabled={disabled}>
                    <Tooltip title={finallyValue} disabled={isDraggingRef.current}>
                        <SCxSliderMark aria-disabled={disabled} ref={markRef} />
                    </Tooltip>
                </SCxSliderTrack>
            </SCxContainer>
        </SCxInactiveArea>
    )
}
